import {z} from 'zod';
import {ObjectIdSchema} from '../../zod';
import {ApiDefinition, BatchedApiDefinition} from '../definitions';
import {
  LegacyAiResearchFieldModeSchema,
  GptModelSchema,
  GptToolSchema,
} from '../../enrichment';
import {GenericTaskSchema} from '../../task';
import {KeyplayUser, KeyplayUserSchema} from '../../auth';
import {PlanSchema} from '../../customer';

export const GetResearchPrompt = {
  path: 'debug/researchPrompt',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    plan: z.string(),
    mode: LegacyAiResearchFieldModeSchema,
    accountId: ObjectIdSchema,
  }),
  responseDataSchema: z
    .object({
      prompt: z.string(),
    })
    .nullable(),
} satisfies ApiDefinition;

export const GetAiDebugCompletion = {
  path: 'debug/aiCompletion',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    model: GptModelSchema,
    prompt: z.string(),
    tools: GptToolSchema.array().min(1).optional(),
    temperature: z.number().min(0).max(2).optional(),
    // TODO: allow JSON schema that we convert to zod schema?
  }),
  responseDataSchema: z
    .object({
      result: z.string(),
      messages: z.unknown().array(),
      usage: z.unknown(),
    })
    .nullable(),
} satisfies ApiDefinition;

export const GetTasks = {
  path: 'debug/tasks',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    partialCustomerName: z.string().optional(),
  }),
  responseDataSchema: GenericTaskSchema.array(),
} satisfies ApiDefinition;

export const CancelTask = {
  path: 'debug/cancelTask',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    taskName: z.string(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetKeyplayUsersMaxBatchSize = 50;
export const GetKeyplayUsers: BatchedApiDefinition<
  {userIds: string[]},
  KeyplayUser[],
  string,
  KeyplayUser
> = {
  path: 'debug/users',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    userIds: z.string().array(),
  }),
  responseDataSchema: KeyplayUserSchema.array(),
  itemSchema: z.string(),
  itemResponseDataSchema: KeyplayUserSchema,

  maxBatchSize: GetKeyplayUsersMaxBatchSize,
  createParams: (userIds) => ({
    userIds,
  }),
  resolveItem: (users, userId) => users.find(({id}) => id === userId) ?? null,
};

export const GetCustomers = {
  path: 'debug/customers',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({}),
  responseDataSchema: z
    .object({
      _id: ObjectIdSchema,
      name: z.string(),
      plan: PlanSchema,
    })
    .array(),
} satisfies ApiDefinition;

export const DeleteCustomerAccounts = {
  path: 'debug/deleteCustomerAccounts',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.object({customerId: ObjectIdSchema}),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;
